/** Shared by lab and med */

import { StateCreator } from "zustand";
import { produce } from "immer";
import { ConnectStoreUnion } from ".";
import { supabase, Tables } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";

export interface VendorBrand {
	id: string;
	name: string;
}

interface ConnectState {
	connectedVendors: string[];
	brandsByOrgId: Record<string, VendorBrand>;
}

export const initialConnectState: ConnectState = {
	connectedVendors: [],
	brandsByOrgId: {},
};

export interface ConnectStoreSlice extends ConnectState {
	// "connectedLabs" act as vendors; we only need connected labs because
	// we only want to submit forms for vendors
	fetchConnectedVendors: (myOrgId: string) => Promise<void>;
	fetchBrands: () => Promise<void>;
	connectVendeeAsClient: (otherOrgId: string, clientId: string) => Promise<boolean>;
}

export const createConnectStore: StateCreator<
	ConnectStoreUnion,
	[],
	[],
	ConnectStoreSlice
> = (set, get) => ({
	...initialConnectState,
	fetchConnectedVendors: async (myOrgId: string) => {
		const { data, error } = await supabase.rpc("get_connected_vendors", {
			my_org_id: myOrgId,
		});

		// We could in theory also select all because RLS takes care of the filtering for us, using
		// the get_connected_vendors function to only allow selecting brands from connected labs
		const { data: brandData, error: brandError } = await supabase
			.from(SupabaseTableEnum.BRANDS)
			.select("*")
			.in("organization_id", [...(data?.map((row) => row.org_id) ?? []), myOrgId]);

		if (error) {
			Logger.error(error.message);
		}

		if (brandError) {
			Logger.error(brandError.message);
		}

		const brandsByOrgId = brandData?.reduce(
			(acc, row) => {
				acc[row.organization_id] = row;
				return acc;
			},
			{} as Record<string, Tables<"brands">>
		);

		set(
			produce((state) => {
				state.connectedVendors = data?.map((row) => row.org_id) ?? [];
				state.brandsByOrgId = { ...state.brandsByOrgId, ...brandsByOrgId ?? {} }
			})
		);
	},
	connectVendeeAsClient: async (otherOrgId: string, clientId: string) => {
		if (!otherOrgId || !clientId) {
			Logger.error(`otherOrgId or clientId is not set: ${otherOrgId} ${clientId}`);
			return false;
		}

		const { error: connectClientError } = await supabase
			.from(SupabaseTableEnum.CLIENTS)
			.update({
				connected_organization_id: otherOrgId
			})
			.eq("id", clientId);

		if (connectClientError) {
			Logger.error(connectClientError.message);
			return false;
		}

		return true;
	},
	fetchBrands: async () => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.BRANDS)
			.select("*");

		if (error) {
			Logger.error(error.message);
		}

		const brandsByOrgId = data?.reduce(
			(acc, row) => {
				acc[row.organization_id] = row;
				return acc;
			},
			{} as Record<string, Tables<"brands">>
		);

		set(
			produce((state) => {
				state.brandsByOrgId = brandsByOrgId;
			})
		);
	},
});
